/**
 * Sample Records List Page
 * Displays a list of sample records with pagination, filtering, and status indicators.
 * Allows navigation to detail pages and creation of new records.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useSampleRecordsList, type SampleRecordView } from '@/hooks/useLab';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { canReadSampleRecord, canWriteSampleRecord } from '@/lib/labPermissions';

type SampleStatus = 'collected' | 'received' | 'rejected' | 'retained' | 'discarded';

const STATUS_LABELS: Record<SampleStatus, string> = {
  collected: '已采集',
  received: '已接收',
  rejected: '已拒收',
  retained: '已保留',
  discarded: '已销毁',
};

const STATUS_CLASSES: Record<SampleStatus, string> = {
  collected:
    'inline-block rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-900',
  received:
    'inline-block rounded-full border border-green-200 bg-green-50 px-3 py-1 text-xs font-semibold text-green-900',
  rejected:
    'inline-block rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-semibold text-red-900',
  retained:
    'inline-block rounded-full border border-yellow-200 bg-yellow-50 px-3 py-1 text-xs font-semibold text-yellow-900',
  discarded:
    'inline-block rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-900',
};

export default function SampleRecordsListPage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const { fetch: fetchList, loading: listLoading } = useSampleRecordsList();

  const [samples, setSamples] = useState<SampleRecordView[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [statusFilter, setStatusFilter] = useState<SampleStatus | ''>('');
  const [sampleNoFilter, setSampleNoFilter] = useState('');

  // Check permission — wildcard-aware (mirrors backend RBAC)
  if (!canReadSampleRecord(permissions)) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Card className="p-6 text-center">
          <h2 className="text-lg font-semibold">权限不足</h2>
          <p className="text-sm text-muted-foreground">您没有查看样本记录的权限</p>
        </Card>
      </div>
    );
  }

  // Load sample records list
  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await fetchList({
          page,
          limit: pageSize,
          status: statusFilter || undefined,
          sampleNo: sampleNoFilter || undefined,
        });
        if (data) {
          setSamples(data.items || []);
        }
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '加载样本记录失败');
      }
    };
    loadData();
  }, [page, pageSize, statusFilter, sampleNoFilter, fetchList]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">样本记录</h1>
        {canWriteSampleRecord(permissions) && (
          <Button onClick={() => navigate('/lab/sample-records/create')}>
            新建样本记录
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label className="text-sm font-semibold">样本编号</label>
            <Input
              placeholder="搜索样本编号"
              value={sampleNoFilter}
              onChange={(e) => {
                setSampleNoFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div>
            <label className="text-sm font-semibold">状态</label>
            <select
              className="w-full rounded border border-input bg-background px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as SampleStatus | '');
                setPage(1);
              }}
            >
              <option value="">全部状态</option>
              <option value="collected">已采集</option>
              <option value="received">已接收</option>
              <option value="rejected">已拒收</option>
              <option value="retained">已保留</option>
              <option value="discarded">已销毁</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>样本编号</TableHead>
              <TableHead>样本类型</TableHead>
              <TableHead>采集量</TableHead>
              <TableHead>状态</TableHead>
              <TableHead>操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {listLoading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  加载中...
                </TableCell>
              </TableRow>
            ) : samples.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  暂无数据
                </TableCell>
              </TableRow>
            ) : (
              samples.map((sample) => {
                const statusClass = STATUS_CLASSES[sample.status];
                return (
                  <TableRow key={sample.id}>
                    <TableCell className="font-mono">{sample.sampleNo}</TableCell>
                    <TableCell>{sample.sampleType}</TableCell>
                    <TableCell>
                      {sample.collectedAmount} {sample.collectedUnit}
                    </TableCell>
                    <TableCell>
                      <span className={statusClass}>{STATUS_LABELS[sample.status]}</span>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/lab/sample-records/${sample.id}`)}
                      >
                        查看详情
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          第 {page} 页，每页 {pageSize} 条
        </div>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
          >
            上一页
          </Button>
          <Button variant="outline" onClick={() => setPage(page + 1)}>
            下一页
          </Button>
        </div>
      </div>
    </div>
  );
}
